import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  Typography,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Alert,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  FormHelperText,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Card,
  CardContent,
  Checkbox,
  useMediaQuery,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  ArrowRightAlt as ArrowIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import dayjs from "dayjs";
import { cateringService, getMenuItems } from '../../services/cateringService';
import { save_catering_quotations, get_specific_quotation, update_catering_quotation } from '../../services/cateringinfoservice';
import { useNotification } from '../../contexts/NotificationContext';
import { TimePicker } from '@mui/x-date-pickers';
import { useLocation } from "react-router-dom";
import { useNavigate } from 'react-router-dom';
import { useRestaurant } from '../../contexts/RestaurantContext';
import outletService from '../../services/outletService';



// Choice Selection Modal Component
const ChoiceSelectionModal = ({
  open,
  onClose,
  onSave,
  item,
  mainItemName
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [selectedChoice, setSelectedChoice] = useState('');

  // Reset selected choice when modal opens with new item
  useEffect(() => {
    if (open && item) {
      setSelectedChoice('');
    }
  }, [open, item]);

  const handleSave = () => {
    if (selectedChoice && item) {
      onSave({
        ...item,
        selectedChoice: selectedChoice,
        name: item.name || mainItemName,
        displayName: selectedChoice === item.name ?
          item.name :
          `${item.name} (${selectedChoice})`,
        choice: true,
        other_choices: item.other_choices || [],
        selectedChoice: selectedChoice
      });
    }
    onClose();
  };

  if (!item || !item.other_choices || item.other_choices.length === 0) {
    return null;
  }

  const allOptions = [item.name, ...(item.other_choices || [])];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant={isMobile ? "subtitle1" : "h6"}>Select Choice for {item.name}</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3, bgcolor: 'primary.light', color: 'white' }}>
          <Typography variant={isMobile ? "subtitle2" : "subtitle1"} fontWeight="bold">
            {item.name}
          </Typography>
          {item.description && (
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.9, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              {item.description}
            </Typography>
          )}
        </Paper>

        <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
          Please select one of the following options:
        </Typography>

        <RadioGroup
          value={selectedChoice}
          onChange={(e) => setSelectedChoice(e.target.value)}
          sx={{ mt: 2 }}
        >
          {allOptions.map((option, index) => (
            <FormControlLabel
              key={index}
              value={option}
              control={<Radio size={isMobile ? "small" : "medium"} />}
              label={
                <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  {option === item.name ? (
                    <Box component="span" sx={{ fontWeight: 'bold' }}>
                      {option} (Original)
                    </Box>
                  ) : (
                    option
                  )}
                </Typography>
              }
              sx={{
                p: { xs: 1, sm: 2 },
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                mb: 1,
                width: '100%',
                mx: 0,
                bgcolor: option === item.name ? 'grey.50' : 'white',
                '&:hover': {
                  bgcolor: 'action.hover',
                  borderColor: 'primary.main'
                },
                '& .MuiFormControlLabel-label': {
                  width: '100%'
                }
              }}
            />
          ))}
        </RadioGroup>

        {selectedChoice && (
          <Paper sx={{ p: { xs: 1.5, sm: 2 }, mt: 3, bgcolor: 'success.light', color: 'white' }}>
            <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              Selected: <strong>
                {selectedChoice === item.name ?
                  item.name :
                  `${item.name} (${selectedChoice})`
                }
              </strong>
            </Typography>
          </Paper>
        )}
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 } }}>
        <Button onClick={onClose} color="inherit" size={isMobile ? "small" : "medium"}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!selectedChoice}
          startIcon={<SaveIcon />}
          size={isMobile ? "small" : "medium"}
        >
          Confirm Choice
        </Button>
      </DialogActions>
    </Dialog>
  );
};



const MenuSelectionModal = ({
  open,
  onClose,
  onSave,
  initialData = null,
  mainMenuIds,
  setMainMenuIds,
  menuItemIds,
  setMenuItemIds,
  setMenuPrice,
  setMenuQuantity,
  isEditing = false,
  editingMenuId = null,
  allMenuItems = [],
  mainMenus = [],
  choiceItems,
  setChoiceItems,
  // menuItemAddMethod,
  // setMenuItemAddMethod
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchLeft, setSearchLeft] = useState('');
  const [searchRight, setSearchRight] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [quantity, setQuantity] = useState('1');
  const [price, setPrice] = useState('');
  const [cateringRequests, setCateringRequests] = useState([]);
  const [quotationMethod, setQuotationMethod] = useState('manual');
  const [selectedRequest, setSelectedRequest] = useState("");
  const [previousCategoryId, setPreviousCategoryId] = useState(null);
  const [menuItemAddMethod,setMenuItemAddMethod] = useState('manual') ;
  // State for choice selection modal
  const [choiceModalOpen, setChoiceModalOpen] = useState(false);
  const [pendingItem, setPendingItem] = useState(null);
  const [pendingItemType, setPendingItemType] = useState(null);

  // Ref to track initial mount
  const isInitialMount = React.useRef(true);

  // Ref to track manually removed item IDs
  const removedItemIds = React.useRef(new Set());

  // Reset removed items tracking when modal opens
  useEffect(() => {
    if (open) {
      removedItemIds.current = new Set();
    }
  }, [open]);

  // Main editing useEffect
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (!isEditing) return;
    }

    if (initialData && isEditing && open) {
      const mainMenu = initialData.main_menu || initialData;
      const categoryId = mainMenu?.id || mainMenu?.categoryId || '';
      setSelectedCategory(categoryId);
      setPreviousCategoryId(categoryId);

      const categoryChoiceItems = Array.isArray(choiceItems[categoryId]) ? choiceItems[categoryId] : [];

      let menuItems = [];
      if (mainMenu.menuItems && Array.isArray(mainMenu.menuItems)) {
        menuItems = mainMenu.menuItems;
      } else if (mainMenu.menu_items && Array.isArray(mainMenu.menu_items)) {
        menuItems = mainMenu.menu_items;
      } else if (initialData.menuItems && Array.isArray(initialData.menuItems)) {
        menuItems = initialData.menuItems;
      }

      const transformedExistingItems = menuItems.map(item => {
        const itemId = item.catering_item?.id || item.id || item.catering_item_id;
        const choiceItem = categoryChoiceItems.find(ci => String(ci.id) === String(itemId));
        const selectedChoice = choiceItem?.selected || item.selectedChoice || null;

        let baseName = '';
        let baseDescription = '';
        let otherChoices = [];
        let isChoice = false;

        if (item.catering_item) {
          baseName = item.catering_item.name;
          baseDescription = item.catering_item.description || '';
          otherChoices = item.catering_item.other_choices || [];
          isChoice = item.catering_item.choice || false;
        } else {
          baseName = item.name || '';
          baseDescription = item.description || '';
          otherChoices = item.other_choices || [];
          isChoice = item.choice || false;
        }

        let displayName = baseName;
        if (selectedChoice) {
          displayName = selectedChoice === baseName
            ? baseName
            : `${baseName} (${selectedChoice})`;
        }

        return {
          id: itemId,
          name: baseName,
          description: baseDescription,
          choice: isChoice,
          other_choices: otherChoices,
          selectedChoice: selectedChoice,
          displayName: displayName,
          fromCategory: categoryId,
          catering_item_id: itemId,
          catering_item: {
            id: itemId,
            name: baseName,
            description: baseDescription,
            choice: isChoice,
            other_choices: otherChoices
          }
        };
      }).filter(item => item.id);

      const existingItemIds = new Set(
        transformedExistingItems.map(item => String(item.id))
      );

      const orphanedChoiceItems = categoryChoiceItems.filter(
        choiceItem => !existingItemIds.has(String(choiceItem.id))
      );

      const orphanedItems = orphanedChoiceItems.map(choiceItem => {
        const fullItem = allMenuItems.find(item => String(item.id) === String(choiceItem.id));

        if (fullItem) {
          return {
            id: fullItem.id,
            name: fullItem.name,
            description: fullItem.description || '',
            choice: fullItem.choice || false,
            other_choices: fullItem.other_choices || [],
            selectedChoice: choiceItem.selected,
            displayName: `${fullItem.name} (${choiceItem.selected})`,
            fromCategory: categoryId,
            catering_item_id: fullItem.id,
            catering_item: {
              id: fullItem.id,
              name: fullItem.name,
              description: fullItem.description || '',
              choice: fullItem.choice || false,
              other_choices: fullItem.other_choices || []
            }
          };
        } else {
          return {
            id: choiceItem.id,
            name: `Item ${choiceItem.id}`,
            description: '',
            choice: true,
            other_choices: [],
            selectedChoice: choiceItem.selected,
            displayName: `Item ${choiceItem.id} (${choiceItem.selected})`,
            fromCategory: categoryId,
            catering_item_id: choiceItem.id,
            catering_item: {
              id: choiceItem.id,
              name: `Item ${choiceItem.id}`,
              description: '',
              choice: true,
              other_choices: []
            }
          };
        }
      });

      const allItems = [...transformedExistingItems, ...orphanedItems]
        .filter(item => !removedItemIds.current.has(String(item.id)));

      setSelectedItems(allItems);

      const menuQuantity = mainMenu.quantity || initialData.quantity || 1;
      const menuPrice = mainMenu.price_per_person || mainMenu.price || initialData.price_per_person || initialData.price || 0;
      setQuantity(menuQuantity.toString());
      setPrice(menuPrice.toString());
      setQuotationMethod(mainMenu.method || initialData.method || 'manual');
    }
  }, [initialData, isEditing, open, choiceItems, allMenuItems]);


  // Sync choice selections useEffect
  useEffect(() => {
    if (isEditing && open && selectedCategory && choiceItems[selectedCategory]) {
      const updatedChoiceItems = Array.isArray(choiceItems[selectedCategory])
        ? choiceItems[selectedCategory]
        : [];

      if (updatedChoiceItems.length > 0) {
        setSelectedItems(prevItems => {
          return prevItems
            .filter(item => {
              const id = item.id || item.catering_item?.id;
              return !removedItemIds.current.has(String(id));
            })
            .map(item => {
              const itemId = item.id || item.catering_item?.id;
              const choiceItem = updatedChoiceItems.find(
                ci => String(ci.id) === String(itemId)
              );

              if (choiceItem && choiceItem.selected) {
                const baseName = item.name || item.catering_item?.name;
                const displayName = choiceItem.selected === baseName
                  ? baseName
                  : `${baseName} (${choiceItem.selected})`;

                return {
                  ...item,
                  selectedChoice: choiceItem.selected,
                  displayName: displayName
                };
              }
              return item;
            });
        });
      }
    }
  }, [choiceItems, selectedCategory, isEditing, open]);


  useEffect(() => {
    async function getCateringData() {
      try {
        const requestResponse = await cateringService.getCateringRequests();
        setCateringRequests(requestResponse);
      } catch (error) {
        // console.log("Error while getting Requests => ", error);
      }
    }
    getCateringData();
  }, []);


  const handleRequestChange = async (e) => {
    setSelectedRequest(e.target.value);
    try {
      const response = await cateringService.getSpecificCateringRequest(e.target.value);
    } catch (error) {
      console.error("Error on Catering Request => ", error);
    }
  };


  const getCategoryItems = () => {
    if (!selectedCategory) return [];
    const category = mainMenus.find(cat => cat.id === selectedCategory);
    return category ? category.menu_items : [];
  };

  const getLeftItems = () => {
    const items = getCategoryItems();
    const selectedIds = selectedItems
      .map(item => item.id || item.catering_item?.id)
      .filter(id => id);

    return items.filter(item =>
      item &&
      item.catering_item &&
      item.catering_item.id &&
      !selectedIds.includes(item.catering_item.id) &&
      item.catering_item.name &&
      item.catering_item.name.toLowerCase().includes(searchLeft.toLowerCase())
    );
  };

  const getRightItems = () => {
    const selectedIdSet = new Set(
      selectedItems
        .map(item => {
          if (item.catering_item && item.catering_item.id) return item.catering_item.id;
          return item.id || item.catering_item_id;
        })
        .filter(id => id)
    );

    return allMenuItems.filter(item =>
      item &&
      item.id &&
      !selectedIdSet.has(item.id) &&
      item.name &&
      item.name.toLowerCase().includes(searchRight.toLowerCase())
    );
  };

  const handleSelectLeftItem = (item) => {
    if (!item || !item.catering_item || !item.catering_item.id) return;

    const itemId = item.catering_item.id;
    const isAlreadySelected = selectedItems.some(selectedItem =>
      (selectedItem.catering_item && selectedItem.catering_item.id === itemId) ||
      selectedItem.id === itemId ||
      selectedItem.catering_item_id === itemId
    );

    if (!isAlreadySelected) {
      removedItemIds.current.delete(String(itemId));

      if (
        item.catering_item.choice &&
        item.catering_item.other_choices &&
        item.catering_item.other_choices.length > 0
      ) {
        setPendingItem({
          id: itemId,
          name: item.catering_item.name,
          description: item.catering_item.description,
          choice: item.catering_item.choice,
          other_choices: item.catering_item.other_choices || [],
          originalItem: item.catering_item,
          fromCategory: selectedCategory
        });
        setPendingItemType('left');
        setChoiceModalOpen(true);
      } else {
        const newItem = {
          ...item,
          id: itemId,
          name: item.catering_item.name,
          description: item.catering_item.description,
          choice: false,
          other_choices: [],
          displayName: item.catering_item.name,
          fromCategory: selectedCategory,
          catering_item_id: itemId,
          catering_item: {
            ...item.catering_item,
            id: itemId,
            name: item.catering_item.name,
            description: item.catering_item.description,
            choice: false,
            other_choices: []
          }
        };
        setSelectedItems(prev => [...prev, newItem]);
      }
    }
  };

  const handleSelectRightItem = (item) => {
    if (!item || !item.id) return;

    const isAlreadySelected = selectedItems.some(selectedItem =>
      (selectedItem.catering_item && selectedItem.catering_item.id === item.id) ||
      selectedItem.id === item.id ||
      selectedItem.catering_item_id === item.id
    );

    if (!isAlreadySelected) {
      removedItemIds.current.delete(String(item.id));

      if (item.choice && item.other_choices && item.other_choices.length > 0) {
        setPendingItem({
          id: item.id,
          name: item.name,
          description: item.description,
          choice: item.choice,
          other_choices: item.other_choices || [],
          originalItem: item,
          fromCategory: selectedCategory
        });
        setPendingItemType('right');
        setChoiceModalOpen(true);
      } else {
        const newItem = {
          ...item,
          id: item.id,
          name: item.name,
          description: item.description,
          choice: false,
          other_choices: [],
          displayName: item.name,
          catering_item_id: item.id,
          fromCategory: selectedCategory,
          catering_item: {
            ...item,
            id: item.id,
            name: item.name,
            description: item.description,
            choice: false,
            other_choices: []
          }
        };
        setSelectedItems(prev => [...prev, newItem]);
      }
    }
  };

  const handleChoiceSave = (itemWithChoice) => {
    const currentCategory = selectedCategory;
    const categoryKey = currentCategory;

    const itemToAdd = {
      id: itemWithChoice.id,
      name: itemWithChoice.name,
      description: itemWithChoice.description,
      choice: true,
      other_choices: itemWithChoice.other_choices,
      selectedChoice: itemWithChoice.selectedChoice,
      displayName: itemWithChoice.displayName,
      originalItem: itemWithChoice.originalItem,
      fromCategory: categoryKey,
      catering_item_id: itemWithChoice.id,
      catering_item: {
        id: itemWithChoice.id,
        name: itemWithChoice.name,
        description: itemWithChoice.description,
        choice: true,
        other_choices: itemWithChoice.other_choices
      }
    };

    setChoiceItems(prevItems => {
      const currentCategoryItems = prevItems[categoryKey];
      const safeCategoryItems = Array.isArray(currentCategoryItems) ? currentCategoryItems : [];
      const existsIndex = safeCategoryItems.findIndex(item => item.id === itemToAdd.id);

      let updatedCategoryItems;
      if (existsIndex !== -1) {
        updatedCategoryItems = [...safeCategoryItems];
        updatedCategoryItems[existsIndex] = {
          id: itemToAdd.id,
          selected: itemWithChoice.selectedChoice
        };
      } else {
        updatedCategoryItems = [...safeCategoryItems, {
          id: itemToAdd.id,
          selected: itemWithChoice.selectedChoice
        }];
      }

      return { ...prevItems, [categoryKey]: updatedCategoryItems };
    });

    setSelectedItems(prevItems => {
      const existsIndex = prevItems.findIndex(item =>
        item.id === itemToAdd.id ||
        (item.catering_item && item.catering_item.id === itemToAdd.id) ||
        item.catering_item_id === itemToAdd.id
      );

      if (existsIndex !== -1) {
        const updatedItems = [...prevItems];
        updatedItems[existsIndex] = {
          ...updatedItems[existsIndex],
          selectedChoice: itemWithChoice.selectedChoice,
          displayName: itemWithChoice.displayName,
          choice: true
        };
        return updatedItems;
      } else {
        return [...prevItems, itemToAdd];
      }
    });

    setChoiceModalOpen(false);
    setPendingItem(null);
    setPendingItemType(null);
  };

  const handleRemoveItem = (itemId) => {
    if (!itemId) return;

    removedItemIds.current.add(String(itemId));

    const itemToRemove = selectedItems.find(item => {
      const ids = [
        item.id,
        item.catering_item?.id,
        item.catering_item_id
      ].filter(Boolean);
      return ids.some(id => String(id) === String(itemId));
    });

    if (!itemToRemove) return;

    const categoryId = itemToRemove.fromCategory;

    setSelectedItems(prevItems =>
      prevItems.filter(item => {
        const ids = [
          item.id,
          item.catering_item?.id,
          item.catering_item_id
        ].filter(Boolean);
        return !ids.some(id => String(id) === String(itemId));
      })
    );

    if (categoryId) {
      setChoiceItems(prevItems => {
        const categoryItems = prevItems[categoryId];
        const safeCategoryItems = Array.isArray(categoryItems) ? categoryItems : [];

        return {
          ...prevItems,
          [categoryId]: safeCategoryItems.filter(
            item => String(item.id) !== String(itemId)
          )
        };
      });
    }
  };

  const handleSave = () => {
    const numericQuantity = quantity === '' || quantity === '.' || quantity === '-'
      ? 1 : parseFloat(quantity) || 1;
    const numericPrice = price === '' || price === '.' || price === '-'
      ? 0 : parseFloat(price) || 0;

    const menuData = {
      id: selectedCategory,
      name: mainMenus.find(c => c.id === selectedCategory)?.name || 'Mixed Items',
      menuItems: selectedItems.map(item => ({
        ...item,
        id: item.id || item.catering_item?.id,
        name: item.name || item.catering_item?.name || 'Unnamed Item',
        description: item.description || item.catering_item?.description || '',
        choice: item.choice || item.catering_item?.choice || false,
        other_choices: item.other_choices || item.catering_item?.other_choices || [],
        selectedChoice: item.selectedChoice,
        displayName: item.displayName || item.name || item.catering_item?.name
      })),
      quantity: numericQuantity,
      price_per_person: numericPrice,
      total: numericPrice * numericQuantity,
      createdAt: initialData?.createdAt || new Date().toISOString(),
      method: quotationMethod,
      menu_addition_method: "manual"

    };
    setMenuItemAddMethod((prev) => ({
      ...prev,
      [menuData.id]: 'manual',
    }));
    // console.log("Addition Method => ", menuData)

    if (isEditing && previousCategoryId) {
      setMainMenuIds(prev => {
        const newMainMenuIds = prev.filter(id => id !== previousCategoryId);
        return [...newMainMenuIds, selectedCategory];
      });

      setMenuItemIds(prevState => {
        const newState = { ...prevState };
        delete newState[previousCategoryId];
        const selectedItemIds = selectedItems
          .map(item => item.id || item.catering_item?.id)
          .filter(id => id !== undefined && id !== null);
        newState[selectedCategory] = selectedItemIds;
        return newState;
      });

      setMenuPrice(prev => {
        const newState = { ...prev };
        delete newState[previousCategoryId];
        newState[selectedCategory] = numericPrice;
        return newState;
      });

      setMenuQuantity(prev => {
        const newState = { ...prev };
        delete newState[previousCategoryId];
        newState[selectedCategory] = numericQuantity;
        return newState;
      });
    } else {
      setMainMenuIds(prev => [...prev, selectedCategory]);

      const selectedItemIds = selectedItems
        .map(item => item.id || item.catering_item?.id)
        .filter(id => id !== undefined && id !== null);

      setMenuItemIds(prevState => {
        const newState = { ...prevState };
        if (newState[selectedCategory]) {
          newState[selectedCategory] = [...newState[selectedCategory], ...selectedItemIds];
        } else {
          newState[selectedCategory] = selectedItemIds;
        }
        return newState;
      });

      setMenuPrice(prev => ({ ...prev, [selectedCategory]: numericPrice }));
      setMenuQuantity(prev => ({ ...prev, [selectedCategory]: numericQuantity }));
    }
    setSelectedCategory('')
    setSelectedItems([]);
    onClose();
    onSave(menuData);
  };

  const numericQuantity = quantity === '' || quantity === '.' || quantity === '-'
    ? 1 : parseFloat(quantity) || 1;
  const numericPrice = price === '' || price === '.' || price === '-'
    ? 0 : parseFloat(price) || 0;
  const totalAmount = numericPrice * numericQuantity;

  const getDisplayName = (item) => {
    if (item.displayName) return item.displayName;
    if (item.selectedChoice) {
      const baseName = item.name || item.catering_item?.name || 'Unnamed Item';
      return item.selectedChoice === baseName
        ? baseName
        : `${baseName} (${item.selectedChoice})`;
    }
    if (item.catering_item) return item.catering_item.name || 'Unnamed Item';
    return item.name || 'Unnamed Item';
  };

  const handleNumberInput = (value, setter) => {
    if (value === '') { setter(''); return; }
    const decimalRegex = /^-?\d*\.?\d*$/;
    if (decimalRegex.test(value)) {
      if (value === '.' || value === '-.' || value.startsWith('..')) return;
      const decimalCount = (value.match(/\./g) || []).length;
      if (decimalCount > 1) return;
      if (value.includes('-') && !value.startsWith('-')) return;
      setter(value);
    }
  };

  const handleNumberBlur = (value, setter) => {
    if (value === '' || value === '-' || value === '.' || value === '-.') {
      setter('');
    } else if (value.endsWith('.') || value.endsWith('.0')) {
      const numericValue = parseFloat(value);
      if (!isNaN(numericValue)) setter(numericValue.toString());
    }
  };

  const isQuantityValid = quantity !== '' && quantity !== '.' && quantity !== '-' && !isNaN(parseFloat(quantity));
  const isPriceValid = price !== '' && price !== '.' && price !== '-' && !isNaN(parseFloat(price));

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { maxHeight: { xs: '90vh', sm: '85vh' } } }}
      >
        <DialogTitle sx={{ pb: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant={isMobile ? "subtitle1" : "h6"}>
              {isEditing ? 'Edit Menu Selection' : 'Add Menu Selection'}
            </Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            {/* Top Section */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Grid container spacing={{ xs: 1.5, sm: 2 }} alignItems="center">
                  <Grid item xs={12} sm={12} md={5}>
                    <FormControl fullWidth size="small">
                      <InputLabel sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Select Main Menu Category</InputLabel>
                      <Select
                        value={selectedCategory}
                        onChange={(e) => {
                          const newCategoryId = e.target.value;
                          setSelectedCategory(newCategoryId);

                          const selectedMenu = mainMenus.find(menu => menu.id === newCategoryId);
                          if (selectedMenu) {
                            setPrice(selectedMenu.price_per_person);
                          }

                          if (!isEditing || previousCategoryId !== newCategoryId) {
                            setSelectedItems([]);
                            removedItemIds.current = new Set();
                          }

                          const menuItems = mainMenus.find(menu => menu.id === newCategoryId);
                          if (menuItems && menuItems.menu_items && !isEditing) {
                            const transformedItems = menuItems.menu_items.map(item => ({
                              ...item,
                              id: item.catering_item?.id,
                              name: item.catering_item?.name,
                              description: item.catering_item?.description,
                              choice: item.catering_item?.choice || false,
                              other_choices: item.catering_item?.other_choices || [],
                              fromCategory: newCategoryId,
                              catering_item_id: item.catering_item?.id,
                              catering_item: {
                                ...item.catering_item,
                                id: item.catering_item?.id,
                                name: item.catering_item?.name,
                                description: item.catering_item?.description,
                                choice: item.catering_item?.choice,
                                other_choices: item.catering_item?.other_choices
                              }
                            })).filter(item => item.id);
                            setSelectedItems(transformedItems);
                          }
                          setSearchLeft('');
                          setSearchRight('');
                        }}
                        label="Select Main Menu Category"
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      >
                        <MenuItem value=""><em>Select a category</em></MenuItem>
                        {mainMenus.map(category => (
                          <MenuItem
                            key={category.id}
                            value={category.id}
                            disabled={mainMenuIds.includes(category.id)}
                            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                          >
                            {category.name}

                            {mainMenuIds.includes(category.id) && (
                              <Chip
                                sx={{
                                  ml: 1,
                                  opacity: 2,
                                  "&.MuiChip-root": { opacity: 1 }
                                }}
                                color="success"
                                label="Selected"
                                size="small"
                              />
                            )}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={6} sm={6} md={2}>
                    <TextField
                      fullWidth
                      label="Quantity"
                      type="text"
                      value={quantity}
                      onChange={(e) => handleNumberInput(e.target.value, setQuantity)}
                      onBlur={(e) => handleNumberBlur(e.target.value, setQuantity)}
                      size="small"
                      error={quantity !== '' && !isQuantityValid}
                      helperText={quantity !== '' && !isQuantityValid ? "Please enter a valid number" : ""}
                      InputProps={{
                        inputProps: { style: { fontSize: '0.75rem' } }
                      }}
                      InputLabelProps={{ style: { fontSize: '0.75rem' } }}
                    />
                  </Grid>

                  <Grid item xs={6} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Price per item"
                      type="text"
                      value={price}
                      onChange={(e) => handleNumberInput(e.target.value, setPrice)}
                      onBlur={(e) => handleNumberBlur(e.target.value, setPrice)}
                      size="small"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">S$</InputAdornment>,
                        inputProps: { style: { fontSize: '0.75rem' } }
                      }}
                      InputLabelProps={{ style: { fontSize: '0.75rem' } }}
                      error={price !== '' && !isPriceValid}
                      helperText={price !== '' && !isPriceValid ? "Please enter a valid number" : ""}
                    />
                  </Grid>

                  <Grid item xs={12} sm={12} md={2}>
                    <Box sx={{ p: { xs: 1, sm: 1.5 }, bgcolor: 'primary.light', borderRadius: 1, textAlign: 'center' }}>
                      <Typography variant="body2" fontWeight="bold" color="white" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' } }}>
                        S${totalAmount.toFixed(2)}
                      </Typography>
                      <Typography variant="caption" color="white" sx={{ fontSize: { xs: '0.6rem', sm: '0.65rem' } }}>Total</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Selected Items Display */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1}>
                  <Typography variant="subtitle2" color="primary" fontWeight="medium" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    Selected Menu Items
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' } }}>
                    {selectedItems.length} items selected
                  </Typography>
                </Box>

                {selectedItems.length === 0 ? (
                  <Box sx={{
                    p: { xs: 3, sm: 4 },
                    border: '2px dashed #e0e0e0',
                    borderRadius: 1,
                    textAlign: 'center',
                    bgcolor: 'grey.50'
                  }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      No items selected yet. Select items from the boxes below.
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{
                    p: { xs: 1.5, sm: 2 },
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    minHeight: 80,
                    bgcolor: 'grey.50'
                  }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {selectedItems.map(item => {
                        const itemId = item.id ?? item.catering_item?.id ?? item.catering_item_id;
                        const displayName = getDisplayName(item);

                        return (
                          <Chip
                            key={itemId}
                            label={displayName}
                            onDelete={() => handleRemoveItem(itemId)}
                            color={item.fromCategory === selectedCategory ? "primary" : "secondary"}
                            variant="outlined"
                            size="small"
                            sx={{
                              fontSize: { xs: '0.7rem', sm: '0.75rem' },
                              '& .MuiChip-deleteIcon': {
                                fontSize: '14px',
                                '&:hover': { color: 'error.main' }
                              }
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Two Column Selection - Stack vertically on mobile */}
            <Grid item xs={12}>
              <Grid container spacing={{ xs: 2, sm: 2 }}>
                {/* Left Column */}
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, height: '100%' }}>
                    <Typography variant="subtitle2" gutterBottom fontWeight="medium" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {selectedCategory
                        ? `${mainMenus.find(c => c.id === selectedCategory)?.name || 'Category'} Items`
                        : 'Select a Category'
                      }
                    </Typography>

                    <TextField
                      fullWidth
                      placeholder="Search items in this category..."
                      value={searchLeft}
                      onChange={(e) => setSearchLeft(e.target.value)}
                      size="small"
                      sx={{ mb: 2 }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                        inputProps: { style: { fontSize: '0.75rem' } }
                      }}
                      disabled={!selectedCategory}
                    />

                    <Box sx={{
                      height: { xs: 250, sm: 200 }, overflow: 'auto', p: 1,
                      border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: 'white'
                    }}>
                      {selectedCategory ? (
                        getLeftItems().length > 0 ? (
                          <Stack spacing={1}>
                            {getLeftItems().map(item => (
                              <Box
                                key={item.catering_item?.id}
                                sx={{
                                  p: { xs: 1, sm: 1.5 }, border: '1px solid #e0e0e0', borderRadius: 1, cursor: 'pointer',
                                  '&:hover': {
                                    borderColor: 'primary.main', bgcolor: 'primary.light',
                                    '& .item-name': { color: 'white' },
                                    '& .item-desc': { color: 'white' }
                                  }
                                }}
                                onClick={() => handleSelectLeftItem(item)}
                              >
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                  <Box flex={1}>
                                    <Typography variant="body2" fontWeight="medium" className="item-name" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                      {item.catering_item?.name || 'Unnamed Item'}
                                      {item.catering_item?.choice && item.catering_item.other_choices?.length > 0 && (
                                        <Chip
                                          label={`${item.catering_item.other_choices.length + 1} options`}
                                          size="small" color="warning" variant="outlined"
                                          sx={{ ml: 1, height: 20, fontSize: '0.6rem' }}
                                        />
                                      )}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" className="item-desc" noWrap sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' } }}>
                                      {item.description || ''}
                                    </Typography>
                                  </Box>
                                  <ArrowIcon color="action" sx={{ ml: 1, fontSize: { xs: 16, sm: 20 } }} />
                                </Box>
                              </Box>
                            ))}
                          </Stack>
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                            <Typography variant="body2" color="text.secondary" align="center" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                              {searchLeft ? 'No matching items found' : 'All items selected'}
                            </Typography>
                          </Box>
                        )
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                          <Typography variant="body2" color="text.secondary" align="center" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                            Please select a category first
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Paper>
                </Grid>

                {/* Right Column */}
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, height: '100%' }}>
                    <Typography variant="subtitle2" gutterBottom fontWeight="medium" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      Other Menu Items
                    </Typography>

                    <TextField
                      fullWidth
                      placeholder="Search other items..."
                      value={searchRight}
                      onChange={(e) => setSearchRight(e.target.value)}
                      size="small"
                      sx={{ mb: 2 }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                        inputProps: { style: { fontSize: '0.75rem' } }
                      }}
                    />

                    <Box sx={{
                      height: { xs: 250, sm: 200 }, overflow: 'auto', p: 1,
                      border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: 'white'
                    }}>
                      {getRightItems().length > 0 ? (
                        <Stack spacing={1}>
                          {getRightItems().map(item => (
                            <Box
                              key={item.id}
                              sx={{
                                p: { xs: 1, sm: 1.5 }, border: '1px solid #e0e0e0', borderRadius: 1, cursor: 'pointer',
                                '&:hover': {
                                  borderColor: 'secondary.main', bgcolor: 'secondary.light',
                                  '& .item-name': { color: 'white' },
                                  '& .item-desc': { color: 'white' }
                                }
                              }}
                              onClick={() => handleSelectRightItem(item)}
                            >
                              <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Box flex={1}>
                                  <Typography variant="body2" fontWeight="medium" className="item-name" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                    {item.name || 'Unnamed Item'}
                                    {item.choice && item.other_choices?.length > 0 && (
                                      <Chip
                                        label={`${item.other_choices.length + 1} options`}
                                        size="small" color="warning" variant="outlined"
                                        sx={{ ml: 1, height: 20, fontSize: '0.6rem' }}
                                      />
                                    )}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" className="item-desc" noWrap sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' } }}>
                                    {item.description || ''}
                                  </Typography>
                                </Box>
                                <ArrowIcon color="action" sx={{ ml: 1, fontSize: { xs: 16, sm: 20 } }} />
                              </Box>
                            </Box>
                          ))}
                        </Stack>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                          <Typography variant="body2" color="text.secondary" align="center" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                            {searchRight ? 'No matching items found' : 'No other items available'}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 }, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={onClose} color="inherit" size={isMobile ? "small" : "medium"}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={selectedItems.length === 0 || !selectedCategory || !isPriceValid || price <= 0}
            size={isMobile ? "small" : "medium"}
          >
            {isEditing ? 'Update Selection' : 'Add to Quotation'}
          </Button>
        </DialogActions>
      </Dialog>

      <ChoiceSelectionModal
        open={choiceModalOpen}
        onClose={() => {
          setChoiceModalOpen(false);
          setPendingItem(null);
          setPendingItemType(null);
        }}
        onSave={handleChoiceSave}
        item={pendingItem}
        mainItemName={pendingItem?.name || ''}
      />
    </>
  );
};

const AddonAdditionComponent = ({ addOnsData, setAddOnsData, addOnPrices, setAddOnPrices, addOnTotal, setAddOnTotal }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [addOnItems, setAddOnItems] = useState([]);
  const [checkedItems, setCheckedItems] = useState({});
  const [expandedAddon, setExpandedAddon] = useState(null);

  const fetchAddOnItems = async () => {
    try {
      const response = await cateringService.getaddonItems();
      setAddOnItems(response.add_on_items || []);
    } catch (error) {
      console.error(error?.message);
    }
  };

  useEffect(() => {
    fetchAddOnItems();
  }, []);

  useEffect(() => {
    if (addOnItems.length > 0) {
      const initialCheckedState = {};
      addOnItems.forEach((item, index) => {
        if (addOnsData && addOnsData[item.id]) {
          initialCheckedState[index] = true;
        }
      });
      setCheckedItems(initialCheckedState);
    }
  }, [addOnItems, addOnsData]);

  useEffect(() => {
    calculateTotal();
  }, [addOnsData, addOnPrices]);

  const calculateTotal = () => {
    let total = 0;
    Object.keys(addOnsData).forEach(id => {
      const quantity = addOnsData[id]?.quantity;
      const price = addOnPrices[id]?.price_per_item;
      if (quantity && price && !isNaN(quantity) && !isNaN(price)) {
        total += quantity * price;
      }
    });
    setAddOnTotal(total);
  };

  const handleCheck = (index, id) => {
    const isChecked = !checkedItems[index];
    setCheckedItems((prev) => ({ ...prev, [index]: isChecked }));

    if (isChecked) {
      setAddOnsData((prev) => ({
        ...prev,
        [id]: { name: addOnItems[index].name, quantity: 1 }
      }));
      setAddOnPrices((prev) => ({
        ...prev,
        [id]: { price_per_item: 0 }
      }));
      setExpandedAddon(id);
    } else {
      setAddOnsData((prev) => {
        const updated = { ...prev };
        delete updated[id];
        return updated;
      });
      setAddOnPrices((prev) => {
        const updated = { ...prev };
        delete updated[id];
        return updated;
      });
      setExpandedAddon(null);
    }
  };

  const handleAddOnQuantityChange = (value, id, name) => {
    if (value === "") {
      setAddOnsData((prev) => ({
        ...prev,
        [id]: { ...prev[id], name: name, quantity: "" }
      }));
      return;
    }
    let numValue = parseInt(value, 10);
    if (isNaN(numValue)) return;
    if (numValue > 5000) numValue = 5000;
    setAddOnsData((prev) => ({
      ...prev,
      [id]: { name: name, quantity: numValue }
    }));
  };

  const handleAddOnPriceChange = (value, id) => {
    if (value === "") {
      setAddOnsData((prev) => ({
        ...prev,
        [id]: { ...prev[id], price: "" }
      }));
      setAddOnPrices((prev) => ({
        ...prev,
        [id]: { price_per_item: 0 }
      }));
      return;
    }
    let numValue = parseFloat(value);
    if (isNaN(numValue)) return;
    if (numValue > 5000) numValue = 5000;
    if (numValue < 0) numValue = 0;
    setAddOnsData((prev) => ({
      ...prev,
      [id]: { ...prev[id], price: value }
    }));
    setAddOnPrices((prev) => ({
      ...prev,
      [id]: { price_per_item: numValue }
    }));
  };

  // For mobile, use Accordion view
  if (isMobile) {
    return (
      <>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {addOnItems.length} add-on items available
          </Typography>
        </Box>
        
        {addOnItems.map((item, index) => {
          const isChecked = checkedItems[index] || false;
          const currentValue = addOnsData[item.id]?.quantity;
          const priceValue = addOnsData[item.id]?.price || addOnPrices[item.id]?.price_per_item;
          const isExpanded = expandedAddon === item.id;

          return (
            <Accordion 
              key={index} 
              expanded={isExpanded}
              onChange={() => setExpandedAddon(isExpanded ? null : item.id)}
              sx={{ mb: 1, borderRadius: 2, '&:before': { display: 'none' } }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <Checkbox
                    checked={isChecked}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleCheck(index, item.id);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    size="small"
                  />
                  <Typography variant="body2" fontWeight={500}>
                    {item.name}
                  </Typography>
                </Box>
              </AccordionSummary>
              
              {isChecked && (
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Quantity
                      </Typography>
                      <TextField
                        size="small"
                        type="text"
                        fullWidth
                        value={currentValue || ''}
                        onChange={(e) => {
                          const inputValue = e.target.value;
                          if (inputValue === "") {
                            handleAddOnQuantityChange("", item.id, item.name);
                            return;
                          }
                          const numberRegex = /^\d*$/;
                          if (numberRegex.test(inputValue) && inputValue !== "0") {
                            const numValue = parseInt(inputValue, 10);
                            if (numValue <= 5000) {
                              handleAddOnQuantityChange(inputValue, item.id, item.name);
                            }
                          }
                        }}
                        placeholder="Quantity"
                      />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Price Per Item
                      </Typography>
                      <TextField
                        size="small"
                        type="text"
                        fullWidth
                        value={priceValue || ''}
                        onChange={(e) => {
                          const inputValue = e.target.value;
                          if (inputValue === "") {
                            handleAddOnPriceChange("", item.id);
                            return;
                          }
                          const decimalRegex = /^\d*\.?\d*$/;
                          if (decimalRegex.test(inputValue)) {
                            const decimalCount = (inputValue.match(/\./g) || []).length;
                            if (decimalCount <= 1 && !(inputValue.length > 1 && inputValue.startsWith('0') && !inputValue.startsWith('0.'))) {
                              handleAddOnPriceChange(inputValue, item.id);
                            }
                          }
                        }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">S$</InputAdornment>,
                        }}
                        placeholder="Price"
                      />
                    </Box>
                  </Box>
                </AccordionDetails>
              )}
            </Accordion>
          );
        })}

        {addOnItems.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No add-on items available
            </Typography>
          </Box>
        )}
      </>
    );
  }

  // Tablet/Desktop grid view
  return (
    <>
      <Grid container spacing={2}>
        {addOnItems.map((item, index) => {
          const isChecked = checkedItems[index] || false;
          const currentValue = addOnsData[item.id]?.quantity;
          const priceValue = addOnsData[item.id]?.price || addOnPrices[item.id]?.price_per_item;

          return (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card sx={{ mb: 2, height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isChecked}
                          onChange={() => handleCheck(index, item.id)}
                        />
                      }
                      label={
                        <Typography variant="body2" fontWeight={500}>
                          {item.name}
                        </Typography>
                      }
                    />

                    {isChecked && (
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 2,
                        mt: 2,
                        flexWrap: 'wrap'
                      }}>
                        <Box sx={{ minWidth: '80px' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 500 }}>
                            Quantity
                          </Typography>
                          <TextField
                            size="small"
                            type="text"
                            value={currentValue || ''}
                            onChange={(e) => {
                              const inputValue = e.target.value;
                              if (inputValue === "") {
                                handleAddOnQuantityChange("", item.id, item.name);
                                return;
                              }
                              const numberRegex = /^\d*$/;
                              if (numberRegex.test(inputValue) && inputValue !== "0") {
                                const numValue = parseInt(inputValue, 10);
                                if (numValue <= 5000) {
                                  handleAddOnQuantityChange(inputValue, item.id, item.name);
                                }
                              }
                            }}
                            onBlur={(e) => {
                              const inputValue = e.target.value;
                              if (inputValue === "" || inputValue === "0") {
                                handleAddOnQuantityChange("", item.id, item.name);
                              } else {
                                const numValue = parseInt(inputValue, 10);
                                if (!isNaN(numValue) && numValue >= 1 && numValue <= 5000) {
                                  handleAddOnQuantityChange(numValue.toString(), item.id, item.name);
                                } else if (numValue > 5000) {
                                  handleAddOnQuantityChange("5000", item.id, item.name);
                                }
                              }
                            }}
                            InputProps={{
                              inputProps: { style: { textAlign: "center", fontSize: "14px", padding: "8px 4px" } }
                            }}
                            sx={{ width: "80px" }}
                          />
                        </Box>

                        <Box sx={{ minWidth: '100px' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 500 }}>
                            Price Per Item
                          </Typography>
                          <TextField
                            size="small"
                            type="text"
                            value={priceValue || ''}
                            onChange={(e) => {
                              const inputValue = e.target.value;
                              if (inputValue === "") {
                                handleAddOnPriceChange("", item.id);
                                return;
                              }
                              const decimalRegex = /^\d*\.?\d*$/;
                              if (decimalRegex.test(inputValue)) {
                                const decimalCount = (inputValue.match(/\./g) || []).length;
                                if (decimalCount <= 1 && !(inputValue.length > 1 && inputValue.startsWith('0') && !inputValue.startsWith('0.'))) {
                                  handleAddOnPriceChange(inputValue, item.id);
                                }
                              }
                            }}
                            onBlur={(e) => {
                              const value = e.target.value;
                              if (value !== "" && !isNaN(parseFloat(value))) {
                                const numValue = parseFloat(value);
                                handleAddOnPriceChange(numValue.toFixed(2), item.id);
                              }
                            }}
                            InputProps={{
                              startAdornment: <InputAdornment position="start" sx={{ ml: 0.5 }}>S$</InputAdornment>,
                              inputProps: { style: { textAlign: "left", fontSize: "14px", padding: "8px 4px 8px 0" } }
                            }}
                            sx={{ width: "100px" }}
                          />
                        </Box>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {addOnItems.length === 0 && (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No add-on items available
          </Typography>
        </Box>
      )}
    </>
  );
};

const QuotationComponent = ({ initialData, onSubmit, onCancel }) => {
  const { cateringOutlets } = useRestaurant()
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  const [formData, setFormData] = useState({
    customerCompanyName: '',
    phoneNumber: '',
    email: '',
    address: '',
    eventDate: null,
    eventTime: null,
    eventDetails: '',
    eventName: '',
    termsConditions: '',
    foodCost: '',
    transportCost: '',
    buffetCost: '',
    discount: '',
    tax: '',
    menuSelections: [],
    notes: '',
    quotation_type: 0,
    total_amount: 0,
    outlet_id: 0,

  });

  const [eventTypeValue, setEventTypeValue] = useState("")

  const [menuModalOpen, setMenuModalOpen] = useState(false);
  const [editingMenuIndex, setEditingMenuIndex] = useState(null);
  const [mainMenuIds, setMainMenuIds] = useState([])
  const [menuItemIds, setMenuItemIds] = useState({})
  const [menuItemAddMethod, setMenuItemAddMethod] = useState({})
  const [price, setPrice] = useState({})
  const [quantity, setQuantity] = useState({})
  const [deliveryOption, setDeliveryOption] = useState("DELIVERY")
  const [quotationMethod, setQuotationMethod] = useState('manual');
  const [selectedRequest, setSelectedRequest] = useState("")
  const [cateringRequests, setCateringRequests] = useState([])
  const [mainMenus, setMainMenus] = useState([])
  const [allMenuItems, setAllMenuItems] = useState([])
  const [requestData, setRequestData] = useState(null)
  const { showSuccess, showError } = useNotification()
  const [mode, setMode] = useState("create")
  const [choiceItems, setChoiceItems] = useState({})
  const [addOns, setAddons] = useState({})
  const [addOnPrices, setAddOnPrices] = useState({})
  // const [menuItemAddMethod,setMenuItemAddMethod] = useState('manual') ;
  const [addOnTotal, setAddOnTotal] = useState(0)
  const [adminUser, setAdminUser] = useState(0)
  const [taxInfo,setTaxInfo] = useState(false)
  const location = useLocation();
  const navigate = useNavigate();
  const quotationId = location.state?.quotationId || null;

  const [isOutletInitialized, setIsOutletInitialized] = useState(false);

  const getOutletDataForNonSuperAdmins = async (outlet_id) => {
    try {
      const response = await outletService.getOutlet(outlet_id)
      let outlet_tax = response?.tax_config?.gst;
      setTaxInfo(response?.inclusive_tax)
      setFormData({ ...formData, outlet_id: outlet_id, tax: outlet_tax });
    }
    catch (error) {
      console.error("Error While Getting Outlet Change ", error);
    }
  }

  useEffect(() => {
    const initializeOutlet = async () => {
      const adminCheck = JSON.parse(localStorage.getItem("admin_outlet_id"));
      if (adminCheck && adminCheck !== 0) {
        await getOutletDataForNonSuperAdmins(adminCheck);
      } else {
        setIsOutletInitialized(true);
      }
    };
    initializeOutlet();
  }, []);

  useEffect(() => {
    const getQuotationForEdit = async (id) => {
      try {
        const response = await get_specific_quotation(id);
        const quotationData = response;

        setAddons(quotationData.add_ons)
        setAddOnPrices(quotationData.add_ons_price)
        setAddOnTotal(quotationData.add_ons_total)
        setDeliveryOption(quotationData.delivery_type)

        let eventDate = null;
        let eventTime = null;

        if (quotationData.event_date) {
          eventDate = new Date(quotationData.event_date);
        }
        if (quotationData.event_time) {
          const [hours, minutes, seconds] = quotationData.event_time.split(':');
          eventTime = new Date();
          eventTime.setHours(parseInt(hours), parseInt(minutes), parseInt(seconds || 0));
        }

        setFormData(prev => ({
          ...prev,
          outlet_id: quotationData.outlet_id,
          customerCompanyName: quotationData.customer_name,
          menuSelections: quotationData.menu_details,
          phoneNumber: quotationData.customer_phone,
          eventDate: eventDate,
          eventTime: eventTime,
          eventDetails: quotationData?.event_details,
          eventName: quotationData.event_name,
          email: quotationData?.customer_email,
          address: quotationData?.venue_address,
          foodCost: quotationData?.food_cost?.toString() || '',
          buffetCost: quotationData?.buffet_cost?.toString() || '',
          transportCost: quotationData?.transport_cost?.toString() || '',
          tax: quotationData?.tax?.toString() || '',
          discount: quotationData?.discount?.toString() || '',
          termsConditions: quotationData?.terms,
        }));

        setEventTypeValue(quotationData.event_type.toLowerCase())

        const main_menu_ids = quotationData.menu_details.map(menu => menu.main_menu.id);
        setMainMenuIds(main_menu_ids);


        let addition_method = quotationData.menu_details.reduce((acc, menu) => {
          acc[menu.main_menu.id] = menu.main_menu.menu_addition_method;
          return acc;
        }, {});

        setMenuItemAddMethod(addition_method);



        const menuItemIdsByMenuId = quotationData.menu_details.reduce((acc, menu) => {
          const menuId = menu.main_menu.id;
          acc[menuId] = menu.main_menu.menu_items.map(item => item.id);
          return acc;
        }, {});
        setMenuItemIds(menuItemIdsByMenuId);

        let updatedPrice = quotationData?.menu_details.reduce((acc, menu) => {
          acc[menu.main_menu.id.toString()] = menu.main_menu.price_per_person;
          return acc;
        }, {});
        setPrice(updatedPrice);

        let updatedQuantity = quotationData?.menu_details.reduce((acc, menu) => {
          acc[menu.main_menu.id.toString()] = menu.main_menu.quantity;
          return acc;
        }, {});
        setQuantity(updatedQuantity);

        let updatedChoiceItems = {};
        if (quotationData.menu_details && Array.isArray(quotationData.menu_details)) {
          quotationData.menu_details.forEach(menu => {
            const menuId = menu.main_menu.id.toString();
            let choiceItemsFromMenu = menu.main_menu.choice_items || [];
            if (Array.isArray(choiceItemsFromMenu) && choiceItemsFromMenu.length > 0) {
              updatedChoiceItems[menuId] = choiceItemsFromMenu.map(item => ({
                id: item.id,
                selected: item.selected
              }));
            } else {
              updatedChoiceItems[menuId] = [];
            }
          });
        }
        setChoiceItems(updatedChoiceItems);
      } catch (error) {
        console.error("Error Get Specific Quotation => ", error);
      }
    };
    if (quotationId) {
      getQuotationForEdit(quotationId)
      setMode("edit")
    }
  }, [])

  useEffect(() => {
    async function getMenuData() {
      try {
        const menusResponse = await cateringService.getMainMenus();
        const allMenuItemsResponse = await cateringService.getMenuItems();
        setMainMenus(menusResponse)
        setAllMenuItems(allMenuItemsResponse)
      }
      catch (error) {
      }
    }
    getMenuData();

    let adminCheck = JSON.parse(localStorage.getItem("admin_outlet_id"))
    if (adminCheck) {
      setFormData((prev) => ({
        ...prev,
        outlet_id: adminCheck
      }));
      getOutletDataForNonSuperAdmins(adminCheck)
    }
  }, []);

  useEffect(() => {
    if (initialData) {
      setFormData({
        ...formData,
        ...initialData,
        customerCompanyName: initialData.customerCompanyName ||
          (initialData.customerName && initialData.companyName ?
            `${initialData.customerName} / ${initialData.companyName}` :
            initialData.customerName || initialData.companyName || ''),
        eventDate: initialData.eventDate ? new Date(initialData.eventDate) : null,
        eventTime: initialData.eventTime ? new Date(`1970-01-01T${initialData.eventTime}`) : null,
        quotation_type: initialData.quotation_type || 0,
        total_amount: initialData.total_amount || 0,
        foodCost: initialData.foodCost?.toString() || '',
        transportCost: initialData.transportCost?.toString() || '',
        buffetCost: initialData.buffetCost?.toString() || '',
        discount: initialData.discount?.toString() || '',
        tax: initialData.tax?.toString() || ''
      });

      setDeliveryOption(initialData.delivery_type || "DELIVERY");

      if (initialData.menuSelections && initialData.menuSelections.length > 0) {
        const mainMenuIdsArray = initialData.menuSelections.map(menu =>
          menu.main_menu?.id || menu.categoryId || menu.id
        ).filter(id => id);
        setMainMenuIds(mainMenuIdsArray);

        const menuItemIdsObj = {};
        const priceObj = {};
        const quantityObj = {};

        initialData.menuSelections.forEach(menu => {
          const menuId = menu.main_menu?.id || menu.categoryId || menu.id;
          if (menuId) {
            const items = menu.menuItems || menu.main_menu?.menuItems || [];
            menuItemIdsObj[menuId] = items
              .map(item => item.id || item.catering_item?.id)
              .filter(id => id);
            priceObj[menuId] = menu.price_per_person || menu.main_menu?.price_per_person || 0;
            quantityObj[menuId] = menu.quantity || menu.main_menu?.quantity || 1;
          }
        });
        setMenuItemIds(menuItemIdsObj);
        setPrice(priceObj);
        setQuantity(quantityObj);
      }
    }

    async function getCateringRequests() {
      try {
        const requestResponse = await cateringService.getCateringRequests();
        setCateringRequests(requestResponse)
      }
      catch (error) {
        console.error(error)
      }
    }
    getCateringRequests()
  }, [initialData]);

  const handleInputChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
  };

  const handlePhoneInputChange = (field, value) => {
    setFormData(prev => (prev ? {
      ...prev,
      phoneNumber: value.target.value
    } : null));
  };

  const handleNumberInput = (field) => (event) => {
    const value = event.target.value;
    if (value === '') {
      setFormData(prev => ({ ...prev, [field]: '' }));
      return;
    }
    const decimalRegex = /^-?\d*\.?\d*$/;
    if (decimalRegex.test(value)) {
      if (value === '.' || value === '-.' || value.startsWith('..')) return;
      const decimalCount = (value.match(/\./g) || []).length;
      if (decimalCount > 1) return;
      if (value.includes('-') && !value.startsWith('-')) return;
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleNumberBlur = (field) => (event) => {
    const value = event.target.value;
    if (value === '' || value === '-' || value === '.' || value === '-.') {
      setFormData(prev => ({ ...prev, [field]: '' }));
    } else if (value.endsWith('.') || value.endsWith('.0')) {
      const numericValue = parseFloat(value);
      if (!isNaN(numericValue)) {
        setFormData(prev => ({ ...prev, [field]: numericValue.toString() }));
      }
    }
  };

  const handleDateChange = (date) => {
    setFormData({
      ...formData,
      eventDate: date
    });
  };

  const handleTimeChange = (time) => {
    if (isDateToday(formData.eventDate) && time && time < new Date()) {
      return;
    }
    setFormData({
      ...formData,
      eventTime: time
    });
  };

  const handleMenuSave = (menuData) => {
    if (editingMenuIndex !== null) {
      const updatedMenuSelections = [...formData.menuSelections];
      updatedMenuSelections[editingMenuIndex] = { main_menu: menuData };
      setFormData({ ...formData, menuSelections: updatedMenuSelections });
    } else {
      setFormData({
        ...formData,
        menuSelections: [...formData.menuSelections, { main_menu: menuData }]
      });
    }
    setMenuModalOpen(false);
    setEditingMenuIndex(null);
  };

  const handleEditMenu = (index) => {
    setEditingMenuIndex(index);
    setMenuModalOpen(true);
  };

  const handleRemoveMenu = (index) => {
    const menuToRemove = formData.menuSelections[index];
    const menuId = menuToRemove?.main_menu?.id || menuToRemove?.categoryId || menuToRemove?.id;

    if (menuId) {
      setMainMenuIds(prev => prev.filter(id => id !== menuId));
      setMenuItemIds(prevState => {
        const newState = { ...prevState };
        delete newState[menuId];
        return newState;
      });
      setPrice(prev => {
        const newState = { ...prev };
        delete newState[menuId];
        return newState;
      });
      setQuantity(prev => {
        const newState = { ...prev };
        delete newState[menuId];
        return newState;
      });
    }
    const updatedMenuSelections = formData.menuSelections.filter((_, i) => i !== index);
    setFormData({ ...formData, menuSelections: updatedMenuSelections });
  };

  const handleRadioChange = (event) => {
    setDeliveryOption(event.target.value)
    if (event.target.value == "DELIVERY") {
      setFormData({ ...formData, buffetCost: 0 });
    }
    else if (event.target.value == "BUFFET") {
      setFormData({ ...formData, buffetCost: 0 });
    }
    else if (event.target.value == "PICKUP") {
      setFormData({ ...formData, buffetCost: 0, transportCost: 0 });
    }
  }

  const handleOutletChange = async (e) => {
    try {
      const response = await outletService.getOutlet(e.target.value)
      let outlet_tax = response?.tax_config?.gst;
      setFormData({ ...formData, outlet_id: e.target.value, tax: outlet_tax });
    }
    catch (error) {
      console.error("Error While Getting Outlet Change ", error);
    }
  }

  const handleRequestChange = async (e) => {
    setSelectedRequest(e.target.value)
    try {
      const response = await cateringService.getSpecificCateringRequest(e.target.value);
      setRequestData(response.data)
      let requestData = response.data
      let eventDate = null;
      let eventTime = null;

      if (response.data.event_date) {
        eventDate = new Date(requestData.event_date);
      }
      if (response.data.event_time) {
        const [hours, minutes, seconds] = requestData.event_time.split(':');
        eventTime = new Date();
        eventTime.setHours(parseInt(hours), parseInt(minutes), parseInt(seconds || 0));
      }

      setFormData(prev => ({
        ...prev,
        customerCompanyName: requestData.customer_name,
        menuSelections: requestData.menu_details,
        phoneNumber: requestData.customer_phone,
        eventDate: eventDate,
        eventTime: eventTime,
        eventName: requestData.event_name,
        email: requestData?.customer_email,
        address: requestData?.venue_address,
        foodCost: requestData?.food_cost?.toString() || '',
        buffetCost: requestData?.buffet_cost?.toString() || '',
        transportCost: requestData?.transport_cost?.toString() || '',
        tax: requestData?.tax?.toString() || '',
        discount: requestData?.discount?.toString() || '',
        outlet_id: requestData?.outlet_id
      }));

      setAddons(requestData.add_ons)
      setAddOnPrices(requestData.add_ons_price)
      setAddOnTotal(requestData.add_ons_total)
      setEventTypeValue(requestData.event_type.toLowerCase())

      if (response.data && response.data.menu_details) {
        const menu_details = response.data.menu_details;
        const transformedMenuSelections = menu_details.map(menu => ({
          main_menu: {
            id: menu.main_menu.id,
            name: menu.main_menu.name,
            menuItems: menu.main_menu.menu_items.map(item => ({
              id: item.id,
              name: item.name,
              description: item.description,
              fromCategory: menu.main_menu.id
            })),
            quantity: menu.main_menu.quantity || 1,
            price_per_person: menu.main_menu.price_per_person || 0,
            total: (menu.main_menu.quantity || 1) * (menu.main_menu.price_per_person || 0),
            method: 'request',
            menu_addition_method: menu.main_menu.menu_addition_method
          }
        }));

        menu_details.map(menu => {
          setMenuItemAddMethod((prev) => ({
            ...prev,
            [menu.main_menu.id]: "request",
          }));
        })

        setFormData(prev => ({
          ...prev,
          menuSelections: transformedMenuSelections
        }));

        const main_menu_ids = menu_details.map(menu => menu.main_menu.id);
        setMainMenuIds(main_menu_ids);

        const menuItemIdsByMenuId = menu_details.reduce((acc, menu) => {
          const menuId = menu.main_menu.id;
          acc[menuId] = menu.main_menu.menu_items.map(item => item.id);
          return acc;
        }, {});
        setMenuItemIds(menuItemIdsByMenuId);

        const priceObj = {};
        const quantityObj = {};
        menu_details.forEach(menu => {
          const menuId = menu.main_menu.id;
          priceObj[menuId] = menu.main_menu.price_per_person || 0;
          quantityObj[menuId] = menu.main_menu.quantity || 1;
        });
        setPrice(priceObj);
        setQuantity(quantityObj);
      }
    }
    catch (error) {
      console.error("Error on Catering Request => ", error)
    }
  }

  const handleQuotationMethodChange = async (value) => {
    if (value === undefined) return;
    
    setFormData(prev => ({
      ...prev,
      customerCompanyName: '',
      phoneNumber: '',
      email: '',
      address: '',
      eventDate: null,
      eventTime: null,
      eventDetails: '',
      eventName: '',
      termsConditions: '',
      foodCost: '',
      transportCost: '',
      buffetCost: '',
      discount: '',
      menuSelections: [],
      notes: '',
      quotation_type: 0,
      total_amount: 0,
    }))

    let adminCheck = JSON.parse(localStorage.getItem("admin_outlet_id"))
    if (adminCheck) {
      setFormData((prev) => ({
        ...prev,
        outlet_id: adminCheck
      }));
    }
    else {
      setFormData((prev) => ({
        ...prev,
        outlet_id: 0,
      }));
    }
    setSelectedRequest("")
    setEventTypeValue("")
    setQuotationMethod(value)
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const menuTotal = formData.menuSelections.reduce((total, menu) => total + (menu.main_menu?.total || menu.total || 0), 0);
      const foodCost = formData.foodCost === '' || formData.foodCost === '.' || formData.foodCost === '-' ? 0 : parseFloat(formData.foodCost) || 0;
      const transportCost = formData.transportCost === '' || formData.transportCost === '.' || formData.transportCost === '-' ? 0 : parseFloat(formData.transportCost) || 0;
      const buffetCost = formData.buffetCost === '' || formData.buffetCost === '.' || formData.buffetCost === '-' ? 0 : parseFloat(formData.buffetCost) || 0;
      const discount = formData.discount === '' || formData.discount === '.' || formData.discount === '-' ? 0 : parseFloat(formData.discount) || 0;
      const tax = formData.tax === '' || formData.tax === '.' || formData.tax === '-' ? 0 : parseFloat(formData.tax) || 0;

      let subtotal = menuTotal + addOnTotal;
      const discountAmount = (subtotal * discount) / 100;
      const afterDiscount = subtotal - discountAmount;
      const taxAmount = (afterDiscount * tax) / 100;
      subtotal += foodCost + transportCost + buffetCost
      const totalAmount = afterDiscount + taxAmount + foodCost + transportCost + buffetCost;

      const formatDate = (date) => {
        if (!date) return '';
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const formatTime = (time) => {
        if (!time) return '';
        const t = new Date(time);
        return t.toTimeString().split(' ')[0];
      };

      if (formData.menuSelections.length === 0) {
        showError("Please Add atleast 1 Menu For Create a Quotation");
        return;
      }
      if (formData.customerCompanyName === "") {
        showError("Please Enter Customer Name / Company Name");
        return;
      }
      if (formData.customerCompanyName.length > 100) {
        showError("Please Enter Customer Name / Company Name less than 100 Characters");
        return;
      }
      if (!formData.eventDate || formData.eventDate === "") {
        showError("Please Select Event Date");
        return;
      }
      if (!formData.eventTime || formData.eventTime === "") {
        showError("Please Select Event Time");
        return;
      }
      if (formData.address.length > 180) {
        showError("Please Give Address Less than 180 Characters");
        return;
      }

      const uniqueMenuIds = [...new Set(mainMenuIds)];

      let quotation_payload_data = {
        "customer_name": formData.customerCompanyName,
        "customer_phone": formData.phoneNumber,
        "outlet_id": formData.outlet_id,
        "customer_email": formData.email,
        "customer_address": formData.address || "",
        "event_date": formatDate(formData.eventDate),
        "event_time": formatTime(formData.eventTime),
        "event_details": formData.eventDetails || "",
        "event_name": formData.eventName,
        "event_type": eventTypeValue,
        "terms": formData.termsConditions || "",
        "main_menu_ids": uniqueMenuIds,
        "menu_item_ids": menuItemIds,
        "price": price,
        "quantity": quantity,
        "food_cost": foodCost,
        "buffet_cost": buffetCost,
        "transport_cost": transportCost,
        "discount": discount.toString(),
        "tax": tax.toString(),
        "quotation_type": quotationMethod === "manual" ? 1 : 0,
        "delivery_type": deliveryOption,
        "total_amount": totalAmount.toFixed(2),
        "request_id": selectedRequest ? [parseInt(selectedRequest)] : [],
        "choice_items": choiceItems,
        "add_ons": addOns,
        "add_ons_prices": addOnPrices,
        "menu_addition_method": menuItemAddMethod,
      };

      if (mode === 'create') {
        try {
          const response = await save_catering_quotations(quotation_payload_data);
          showSuccess("Quotation Saved Successfully")
          handleQuotationMethodChange(quotationMethod)
        }
        catch (error) {
          console.error("Error Happened on Quotation Saving => ", error)
        }
      } else {
        const response = await update_catering_quotation(quotationId, quotation_payload_data);
        showSuccess("Quotation Updated Successfully")
        navigate("/admin/info")
      }

      if (onSubmit) {
        onSubmit({
          ...formData,
          menuTotal,
          subtotal,
          discountAmount,
          taxAmount,
          totalAmount,
          delivery_type: deliveryOption,
          quotation_type: formData.quotation_type,
          status: mode === 'create' ? 'draft' : formData.status
        });
      }
    } catch (error) {
      console.error("Error submitting quotation:", error);
      showError(error)
    }
  };

  const isDateToday = (date) => {
    if (!date) return false;
    const today = new Date();
    const selectedDate = new Date(date);
    return selectedDate.getDate() === today.getDate() &&
      selectedDate.getMonth() === today.getMonth() &&
      selectedDate.getFullYear() === today.getFullYear();
  };

  const getCurrentRoundedTime = () => {
    const now = new Date();
    const minutes = now.getMinutes();
    const roundedMinutes = Math.ceil(minutes / 30) * 30;
    now.setMinutes(roundedMinutes);
    now.setSeconds(0);
    now.setMilliseconds(0);
    return now;
  };

  const calculateTotals = () => {
    const menuTotal = formData.menuSelections.reduce((total, menu) => {
      const price = menu.main_menu?.total || menu.total || 0;
      return total + (price);
    }, 0);
    const foodCost = formData.foodCost === '' || formData.foodCost === '.' || formData.foodCost === '-' ? 0 : parseFloat(formData.foodCost) || 0;
    const transportCost = formData.transportCost === '' || formData.transportCost === '.' || formData.transportCost === '-' ? 0 : parseFloat(formData.transportCost) || 0;
    const buffetCost = formData.buffetCost === '' || formData.buffetCost === '.' || formData.buffetCost === '-' ? 0 : parseFloat(formData.buffetCost) || 0;
    const discount = formData.discount === '' || formData.discount === '.' || formData.discount === '-' ? 0 : parseFloat(formData.discount) || 0;
    const tax = formData.tax === '' || formData.tax === '.' || formData.tax === '-' ? 0 : parseFloat(formData.tax) || 0;

    let subtotal = menuTotal + addOnTotal;
    const discountAmount = (subtotal * discount) / 100;
    const afterDiscount = subtotal - discountAmount;
    const taxAmount = (afterDiscount * tax) / 100;
    subtotal += foodCost + transportCost + buffetCost
    const total = afterDiscount + taxAmount + foodCost + transportCost + buffetCost;
    const additionalCosts = foodCost + transportCost + buffetCost;

    // let subtotal = menuTotal + addOnTotal;

    // const discountAmount = (subtotal * discount) / 100;
    // const afterDiscount = subtotal - discountAmount;

    // const additionalCosts = foodCost + transportCost + buffetCost;

    // // Amount before any tax calculation
    // const amount = afterDiscount + additionalCosts;

    // let taxAmount = 0;
    // let total = 0;

    // if (taxInfo?.inclusive) {
    //   // Tax is already included in the amount
    //   total = amount;
    //   taxAmount = total - total / (1 + tax / 100);
    // } else {
    //   // Tax is added on top
    //   taxAmount = amount * (tax / 100);
    //   total = amount + taxAmount;
    // }

    // // Optional: Subtotal for display
    // console.log("Subtotal => ", subtotal)
    // console.log("Tax Amount => ",taxAmount)
    return { menuTotal, subtotal, discountAmount, taxAmount, total, additionalCosts };
  };

  const totals = calculateTotals();

  const isFoodCostValid = formData.foodCost === '' || formData.foodCost === '.' || formData.foodCost === '-' || !isNaN(parseFloat(formData.foodCost));
  const isTransportCostValid = formData.transportCost === '' || formData.transportCost === '.' || formData.transportCost === '-' || !isNaN(parseFloat(formData.transportCost));
  const isBuffetCostValid = formData.buffetCost === '' || formData.buffetCost === '.' || formData.buffetCost === '-' || !isNaN(parseFloat(formData.buffetCost));
  const isDiscountValid = formData.discount === '' || formData.discount === '.' || formData.discount === '-' || !isNaN(parseFloat(formData.discount));
  const istaxValid = formData.tax === '' || formData.tax === '.' || formData.tax === '-' || !isNaN(parseFloat(formData.tax));

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ 
        p: { xs: 1.5, sm: 2, md: 3 }, 
        maxWidth: 1400, 
        margin: '0 auto',
        width: '100%'
      }}>
        {/* Header */}
        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, boxShadow: 2 }}>
          <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} mb={2} gap={2}>
            <Box>
              <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold" gutterBottom>
                {mode === 'create' ? 'Create New Quotation' : 'Edit Quotation'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
              <Button
                startIcon={<CancelIcon />}
                onClick={() => navigate("/admin/info")}
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                sx={{ flex: { xs: 1, sm: 'none' } }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                form="quotation-form"
                size={isMobile ? "small" : "medium"}
                sx={{ flex: { xs: 1, sm: 'none' } }}
              >
                {mode === 'create' ? 'Create Quotation' : 'Update Quotation'}
              </Button>
            </Box>
          </Box>

          {mode === "create" &&
            <Box mb={3} sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" gutterBottom fontWeight="medium" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  Quotation Method for this selection
                </Typography>
                <ToggleButtonGroup
                  value={quotationMethod}
                  exclusive
                  onChange={(e, value) => handleQuotationMethodChange(value)}
                  aria-label="quotation method"
                  size="small"
                  sx={{ flexWrap: 'wrap', gap: 1 }}
                >
                  <ToggleButton value="manual" aria-label="manual" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                    Manual Selection
                  </ToggleButton>
                  <ToggleButton value="request" aria-label="request" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                    Request Based
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {quotationMethod === "request" &&
                <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
                  <FormControl fullWidth size="small">
                    <Select
                      value={selectedRequest}
                      onChange={handleRequestChange}
                      displayEmpty
                      sx={{ backgroundColor: 'background.paper', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    >
                      <MenuItem value="" disabled>Select Request Number</MenuItem>
                      {cateringRequests.map((request) => (
                        <MenuItem key={request.id} value={request.id}>
                          {request.request_number} - {request.customer_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              }
            </Box>
          }

          <Box sx={{ width: { xs: '100%', sm: '50%', md: '30%' }, mb: 2 }}>
            <FormControl fullWidth size="small">
              <Select
                value={formData.outlet_id}
                onChange={handleOutletChange}
                displayEmpty
                disabled={JSON.parse(localStorage.getItem('admin_outlet_id')) ? true : false}
                sx={{ backgroundColor: 'background.paper', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                <MenuItem value={0} disabled>Select Outlet</MenuItem>
                {cateringOutlets.map((outlet) => (
                  <MenuItem key={outlet.id} value={outlet.id}>
                    {outlet.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <form id="quotation-form" onSubmit={handleSubmit}>
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              {/* Left Column - Customer & Event Details */}
              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, height: '100%' }}>
                  <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom color="primary" fontWeight="medium">
                    Customer & Event Details
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Customer / Company Name"
                        value={formData.customerCompanyName}
                        onChange={handleInputChange('customerCompanyName')}
                        size={isMobile ? "small" : "medium"}
                        placeholder="John Doe / Acme Corporation"
                        inputProps={{ maxLength: 50 }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <PhoneInput
                        country="sg"
                        specialLabel=""
                        value={formData?.phoneNumber || ''}
                        onChange={(phone, country) => handlePhoneInputChange("phone", { target: { value: phone, countryValue: country } })}
                        inputStyle={{
                          width: '100%',
                          height: isMobile ? '36px' : '40px',
                          borderRadius: '7px',
                          color: 'black',
                          paddingLeft: '58px',
                          fontSize: isMobile ? '12px' : '14px',
                        }}
                        buttonStyle={{
                          borderTopLeftRadius: '7px',
                          borderBottomLeftRadius: '7px',
                        }}
                        containerStyle={{
                          width: '100%',
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange('email')}
                        size={isMobile ? "small" : "medium"}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Address"
                        multiline
                        rows={2}
                        value={formData.address}
                        onChange={handleInputChange('address')}
                        size={isMobile ? "small" : "medium"}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DatePicker
                        label="Event Date"
                        format="dd/MM/yyyy"
                        value={formData.eventDate}
                        minDate={new Date()}
                        onChange={(date) => setFormData((prev) => ({
                          ...prev,
                          eventDate: date,
                          eventTime: isDateToday(date) ? getCurrentRoundedTime() : null
                        }))}
                        slotProps={{
                          textField: { 
                            fullWidth: true, 
                            size: isMobile ? "small" : "medium",
                            sx: { '& .MuiInputBase-input': { fontSize: { xs: '0.75rem', sm: '0.875rem' } } }
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TimePicker
                        label="Event Time"
                        value={formData.eventTime}
                        onChange={handleTimeChange}
                        minTime={isDateToday(formData.eventDate) ? new Date() : null}
                        slotProps={{
                          textField: { 
                            fullWidth: true, 
                            size: isMobile ? "small" : "medium",
                            sx: { '& .MuiInputBase-input': { fontSize: { xs: '0.75rem', sm: '0.875rem' } } }
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Event Name"
                        type="text"
                        value={formData.eventName}
                        onChange={handleInputChange('eventName')}
                        size={isMobile ? "small" : "medium"}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                        <Select
                          value={eventTypeValue}
                          onChange={(e) => setEventTypeValue(e.target.value)}
                          displayEmpty
                          sx={{ backgroundColor: 'background.paper', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                        >
                          <MenuItem value="" disabled>Select Event Type</MenuItem>
                          <MenuItem value="wedding" >Wedding</MenuItem>
                          <MenuItem value="birthday" >Birthday</MenuItem>
                          <MenuItem value="corporate" >Corporate</MenuItem>
                          <MenuItem value="anniversary">Anniversary</MenuItem>
                          <MenuItem value="festival">Festival</MenuItem>
                          <MenuItem value="other">Other</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Event Details"
                        multiline
                        rows={3}
                        value={formData.eventDetails}
                        onChange={handleInputChange('eventDetails')}
                        placeholder="Describe the event, theme, special requirements, etc."
                        size={isMobile ? "small" : "medium"}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Terms & Conditions"
                        multiline
                        rows={3}
                        value={formData.termsConditions}
                        onChange={handleInputChange('termsConditions')}
                        placeholder="Payment terms, cancellation policy, special conditions..."
                        size={isMobile ? "small" : "medium"}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Right Column - Cost Calculation */}
              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, height: '100%' }}>
                  <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom color="primary" fontWeight="medium">
                    Cost Calculation
                  </Typography>

                  <FormControl sx={{ mb: 2 }}>
                    <RadioGroup
                      row
                      aria-labelledby="demo-row-radio-buttons-group-label"
                      name="row-radio-buttons-group"
                      value={deliveryOption}
                      onChange={handleRadioChange}
                    >
                      <FormControlLabel value="DELIVERY" control={<Radio size={isMobile ? "small" : "medium"} />} label="Delivery" />
                      <FormControlLabel value="BUFFET" control={<Radio size={isMobile ? "small" : "medium"} />} label="Buffet" />
                      <FormControlLabel value="PICKUP" control={<Radio size={isMobile ? "small" : "medium"} />} label="Pickup" />
                    </RadioGroup>
                  </FormControl>

                  <Stack spacing={2}>
                    {deliveryOption !== "PICKUP" &&
                      <TextField
                        label="Transport Cost"
                        type="text"
                        value={formData.transportCost}
                        onChange={handleNumberInput('transportCost')}
                        onBlur={handleNumberBlur('transportCost')}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">S$</InputAdornment>,
                        }}
                        fullWidth
                        size={isMobile ? "small" : "medium"}
                        error={!isTransportCostValid}
                        helperText={!isTransportCostValid ? "Please enter a valid number" : ""}
                      />
                    }

                    {deliveryOption === "BUFFET" &&
                      <TextField
                        label="Buffet Cost"
                        type="text"
                        value={formData.buffetCost}
                        onChange={handleNumberInput('buffetCost')}
                        onBlur={handleNumberBlur('buffetCost')}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">S$</InputAdornment>,
                        }}
                        fullWidth
                        size={isMobile ? "small" : "medium"}
                        error={!isBuffetCostValid}
                        helperText={!isBuffetCostValid ? "Please enter a valid number" : ""}
                      />}

                    <TextField
                      label="Discount"
                      type="text"
                      value={formData.discount}
                      onChange={handleNumberInput('discount')}
                      onBlur={handleNumberBlur('discount')}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      }}
                      fullWidth
                      size={isMobile ? "small" : "medium"}
                      error={!isDiscountValid}
                      helperText={!isDiscountValid ? "Please enter a valid number" : ""}
                    />

                    <TextField
                      label="Tax"
                      type="text"
                      value={formData.tax}
                      onChange={handleNumberInput('tax')}
                      onBlur={handleNumberBlur('tax')}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      }}
                      fullWidth
                      size={isMobile ? "small" : "medium"}
                      error={!istaxValid}
                      helperText={!istaxValid ? "Please enter a valid number" : ""}
                    />

                    <Divider sx={{ my: 2 }} />

                    {/* Totals Display */}
                    <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: 'grey.50', borderRadius: 1, border: 1, borderColor: 'divider' }}>
                      <Stack spacing={1.5}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                          <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' } }}>Menu Items Total:</Typography>
                          <Typography variant="body2" fontWeight="medium" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' } }}>
                            S${totals.menuTotal.toFixed(2)}
                          </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                          <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' } }}>Add Ons Total:</Typography>
                          <Typography variant="body2" fontWeight="medium" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' } }}>
                            S${addOnTotal.toFixed(2)}
                          </Typography>
                        </Box>
                        {deliveryOption !== "PICKUP" &&
                          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                            <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' } }}>Additional Costs:</Typography>
                            <Typography variant="body2" fontWeight="medium" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' } }}>
                              S${totals.additionalCosts.toFixed(2)}
                            </Typography>
                          </Box>}
                        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                          <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' } }}>Subtotal:</Typography>
                          <Typography variant="body2" fontWeight="medium" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' } }}>
                            S${totals.subtotal.toFixed(2)}
                          </Typography>
                        </Box>
                        {formData.discount > 0 &&
                          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                            <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' } }}>Discount ({parseFloat(formData.discount) || 0}%):</Typography>
                            <Typography variant="body2" color="error" fontWeight="medium" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' } }}>
                              -S${totals.discountAmount.toFixed(2)}
                            </Typography>
                          </Box>}
                        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                          <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' } }}>Tax ({parseFloat(formData.tax) || 0}%):</Typography>
                          <Typography variant="body2" fontWeight="medium" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' } }}>
                            S${totals.taxAmount.toFixed(2)}
                          </Typography>
                        </Box>
                        <Divider />
                        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                          <Typography variant={isMobile ? "subtitle1" : "h6"}>Total Amount:</Typography>
                          <Typography variant={isMobile ? "subtitle1" : "h6"} color="primary" fontWeight="bold">
                            S${totals.total.toFixed(2)}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </form>
        </Paper>

        {/* Menu Selections Section */}
        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, boxShadow: 2, border: 1, borderColor: 'divider' }}>
          <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} mb={3} gap={2}>
            <Box>
              <Typography variant={isMobile ? "subtitle1" : "h6"} color="primary" fontWeight="medium" gutterBottom>
                Menu Selections
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                Add multiple menu selections with different quotation methods
              </Typography>
            </Box>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingMenuIndex(null);
                setMenuModalOpen(true);
              }}
              size={isMobile ? "small" : "medium"}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Add Menu Selection
            </Button>
          </Box>

          {/* Menu Selections Table - Responsive */}
          {formData.menuSelections.length === 0 ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              No menu selections added. Click "Add Menu Selection" to add menus to this quotation.
            </Alert>
          ) : isMobile ? (
            // Mobile Card View for Menu Selections
            <Stack spacing={2}>
              {formData.menuSelections.map((menu, index) => (
                <Card key={menu.main_menu?.id || menu.id || index}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                      <Chip
                        label={menu.main_menu?.menu_addition_method || menu.method || 'manual'}
                        size="small"
                        color={menu.main_menu?.menu_addition_method === 'manual' || menu.method === 'manual' ? 'primary' : 'secondary'}
                        variant="outlined"
                      />
                      <Box>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEditMenu(index)}
                          title="Edit"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveMenu(index)}
                          title="Remove"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                    <Typography variant="body1" fontWeight="bold" gutterBottom>
                      {menu?.main_menu?.name}
                    </Typography>
                    <Box display="flex" justifyContent="space-between" mt={1}>
                      <Typography variant="body2" color="text.secondary">Quantity:</Typography>
                      <Typography variant="body2" fontWeight="medium">{menu?.main_menu?.quantity}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" mt={0.5}>
                      <Typography variant="body2" color="text.secondary">Price per item:</Typography>
                      <Typography variant="body2" fontWeight="medium">S${menu?.main_menu?.price_per_person}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" mt={0.5}>
                      <Typography variant="body2" color="text.secondary">Total:</Typography>
                      <Typography variant="body2" fontWeight="bold" color="primary">S${menu?.main_menu?.total?.toFixed(2)}</Typography>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          ) : (
            // Table View for Tablet/Desktop
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell><Typography variant="subtitle2" fontWeight="bold">Method</Typography></TableCell>
                    <TableCell><Typography variant="subtitle2" fontWeight="bold">Category</Typography></TableCell>
                    <TableCell><Typography variant="subtitle2" fontWeight="bold">Quantity</Typography></TableCell>
                    <TableCell><Typography variant="subtitle2" fontWeight="bold">Price per Item</Typography></TableCell>
                    <TableCell><Typography variant="subtitle2" fontWeight="bold">Total</Typography></TableCell>
                    <TableCell align="center"><Typography variant="subtitle2" fontWeight="bold">Actions</Typography></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData.menuSelections.map((menu, index) => (
                    <TableRow
                      key={menu.main_menu?.id || menu.id || index}
                      hover
                      sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                    >
                      <TableCell>
                        <Chip
                          label={menu.main_menu?.menu_addition_method || menu.method}
                          size="small"
                          color={menu.main_menu?.menu_addition_method === 'manual' || menu.method === 'manual' ? 'primary' : 'secondary'}
                          variant="outlined"
                          sx={{ fontWeight: 'medium' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {menu?.main_menu?.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {menu?.main_menu?.quantity}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          S${menu?.main_menu?.price_per_person}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold" color="primary">
                          S${menu?.main_menu?.total?.toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={1} justifyContent="center">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEditMenu(index)}
                            title="Edit"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveMenu(index)}
                            title="Remove"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {formData.menuSelections.length > 0 && (
            <Box mt={2} pt={2} borderTop={1} borderColor="divider">
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                Total Menu Selections: {formData.menuSelections.length} |
                Total Menu Amount: S${totals.menuTotal.toFixed(2)}
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Add Ons Selections Section */}
        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, boxShadow: 2, border: 1, borderColor: 'divider' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box>
              <Typography variant={isMobile ? "subtitle1" : "h6"} color="primary" fontWeight="medium" gutterBottom>
                Add Ons Selections
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                Add Multiple add ons here
              </Typography>
            </Box>
          </Box>
          <AddonAdditionComponent
            addOnsData={addOns}
            setAddOnsData={setAddons}
            addOnPrices={addOnPrices}
            setAddOnPrices={setAddOnPrices}
            addOnTotal={addOnTotal}
            setAddOnTotal={setAddOnTotal}
          />
        </Paper>
      </Box>

      {/* Menu Selection Modal */}
      <MenuSelectionModal
        open={menuModalOpen}
        onClose={() => {
          setMenuModalOpen(false);
          setEditingMenuIndex(null);
        }}
        onSave={handleMenuSave}
        initialData={editingMenuIndex !== null ? formData.menuSelections[editingMenuIndex] : null}
        mainMenuIds={mainMenuIds}
        setMainMenuIds={setMainMenuIds}
        menuItemIds={menuItemIds}
        setMenuItemIds={setMenuItemIds}
        setMenuPrice={setPrice}
        setMenuQuantity={setQuantity}
        isEditing={editingMenuIndex !== null}
        editingMenuId={editingMenuIndex !== null ?
          formData.menuSelections[editingMenuIndex]?.main_menu?.id ||
          formData.menuSelections[editingMenuIndex]?.id : null}
        allMenuItems={allMenuItems}
        mainMenus={mainMenus}
        choiceItems={choiceItems}
        setChoiceItems={setChoiceItems}
        menuItemAddMethod={menuItemAddMethod}
        setMenuItemAddMethod={setMenuItemAddMethod}
      />
    </LocalizationProvider>
  );
};

export default QuotationComponent;